import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatIcon from "@mui/icons-material/Chat";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PublicIcon from "@mui/icons-material/Public";
import ForumIcon from "@mui/icons-material/Forum";
import InstagramIcon from "@mui/icons-material/Instagram";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid } from "recharts";
import { getOverview, getPlans, getWhatsAppDashboard } from "../api/adminApi";
import ChartCard from "../components/ChartCard";
import DateRangeToggle from "../components/DateRangeToggle";

const StatCardDash = ({ icon, label, value, color }) => (
  <Paper className="glass-card" sx={{ p: 3.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "16px", bgcolor: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ letterSpacing: "0.3px" }}>{label}</Typography>
        <Typography fontWeight={800} fontSize={24} color="#1e293b">{value}</Typography>
      </Box>
    </Box>
  </Paper>
);

const PIE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#3b82f6"];
const STATUS_LABELS = { active: "Active", closed: "Closed", live_requested: "Live Requested" };

const Dashboard = () => {
  const [stats, setStats] = useState({ users: 0, bots: 0, conversations: 0, plans: 0 });
  const [overview, setOverview] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getOverview(days).catch(() => null),
      getPlans().catch(() => []),
      getWhatsAppDashboard().catch(() => null),
    ]).then(([ov, plans, waDash]) => {
      setOverview(ov);
      setPlatformStats(waDash?.stats || null);
      setStats({
        users: ov?.totalUsers || 0,
        bots: ov?.totalBots || 0,
        conversations: ov?.totalConversations || 0,
        plans: plans.length || 0,
      });
    }).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const dailyData = overview?.dailyConversations?.labels?.map((label, i) => ({
    date: label.slice(5),
    conversations: overview.dailyConversations.values[i],
  })) || [];

  const statusData = Object.entries(overview?.statusBreakdown || {}).map(([key, value]) => ({
    name: STATUS_LABELS[key] || key,
    value,
  }));

  const platformBarData = [
    { name: "Website", users: platformStats?.websiteUsers || 0 },
    { name: "WhatsApp", users: platformStats?.totalClients || 0 },
    { name: "Instagram", users: platformStats?.instagramUsers || 0 },
    { name: "AI Agents", users: platformStats?.aiAgentUsers || 0 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Dashboard</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>Platform overview</Typography>
        </Box>
        <DateRangeToggle value={days} onChange={setDays} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: "27px", mb: 5 }}>
        <StatCardDash icon={<PeopleIcon sx={{ color: "#6366f1", fontSize: 24 }} />} label="Total Users" value={stats.users.toLocaleString()} color="#6366f1" />
        <StatCardDash icon={<SmartToyIcon sx={{ color: "#10b981", fontSize: 24 }} />} label="Total Bots" value={stats.bots.toLocaleString()} color="#10b981" />
        <StatCardDash icon={<ChatIcon sx={{ color: "#f59e0b", fontSize: 24 }} />} label="Conversations" value={stats.conversations.toLocaleString()} color="#f59e0b" />
        <StatCardDash icon={<AttachMoneyIcon sx={{ color: "#8b5cf6", fontSize: 24 }} />} label="Active Plans" value={stats.plans} color="#8b5cf6" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: "27px", mb: 5 }}>
          <ChartCard title="Conversation Trend" subtitle="Daily conversations over time" height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="colorConvs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
                <Area type="monotone" dataKey="conversations" stroke="#6366f1" strokeWidth={2} fill="url(#colorConvs)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Status Breakdown" subtitle="Conversation status distribution" height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={95} innerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px", mb: 5 }}>
          <ChartCard title="Platform Users" subtitle="Users per platform" height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={platformBarData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
                <Bar dataKey="users" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr" }, gap: "27px", alignContent: "start" }}>
            {[
              { icon: <PublicIcon sx={{ color: "#6366f1", fontSize: 28 }} />, label: "Website", desc: "Widget platform", color: "#6366f1" },
              { icon: <ForumIcon sx={{ color: "#25D366", fontSize: 28 }} />, label: "WhatsApp", labelIconColor: "#25D366", desc: "Business API", color: "#25D366" },
              { icon: <InstagramIcon sx={{ color: "#e4405f", fontSize: 28 }} />, label: "Instagram", desc: "DM Platform", color: "#e4405f" },
              { icon: <AutoAwesomeIcon sx={{ color: "#8b5cf6", fontSize: 28 }} />, label: "AI Agents", desc: "Conversational AI", color: "#8b5cf6" },
            ].map((p, i) => (
              <Paper key={i} className="glass-card" sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {p.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize={14} color="#1e293b">{p.label}</Typography>
                  <Typography variant="caption" color="#64748b">{p.desc}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
